import { Box, Grid, GridItem } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import type React from 'react';
import {
	MdKeyboardArrowLeft,
	MdKeyboardArrowRight,
	MdKeyboardDoubleArrowLeft,
	MdKeyboardDoubleArrowRight,
} from 'react-icons/md';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import type { DataTablePaginationsProps } from '@/components/Organisms/DataTable/DataTable.types';

const pageSizes: SelectItem[] = [
	{ label: '15', value: 15 },
	{ label: '30', value: 30 },
	{ label: '50', value: 50 },
	{ label: '100', value: 100 },
];

const DataTablePaginations: React.FC<DataTablePaginationsProps> = ({
	paginationKey,
	bg = 'brand.500',
	color = 'white',
	sx,
	pageSize = { label: '15', value: 15 },
	setPageSize,
	page,
	setPage,
	count = 0,
	...rest
}) => {
	const totalPages = Math.ceil(count / Number(pageSize.value));

	const onSelectValueChange = (val: SingleValue<SelectItem>) => {
		setPageSize(val as SelectItem);
		setPage(1);
	};

	const getVisiblePages = () => {
		if (totalPages <= 5)
			return Array.from({ length: totalPages }, (_, i) => i + 1);

		if (page <= 3) return [1, 2, 3, 4, 5];
		if (page >= totalPages - 2)
			return [
				totalPages - 4,
				totalPages - 3,
				totalPages - 2,
				totalPages - 1,
				totalPages,
			];

		return [page - 2, page - 1, page, page + 1, page + 2];
	};

	const visiblePages = getVisiblePages();

	return (
		<Grid
			key={paginationKey}
			templateColumns={{ base: '1fr', md: 'repeat(7, 1fr)' }}
			mt={4}
			gap={2}
			alignItems="center"
			style={sx}
			{...rest}
		>
			<GridItem colSpan={{ base: 1, md: 2 }}>
				<FaceliftText fontSize="sm" textAlign={{ base: 'center', md: 'left' }}>
					Showing Results {(page - 1) * +pageSize.value + 1}-
					{Math.min(page * +pageSize.value, count)} of {count}
				</FaceliftText>
			</GridItem>

			<GridItem colSpan={{ base: 1, md: 3 }} visibility={visiblePages.length ? 'visible' : 'hidden'}>
				<Box display="flex" alignItems="center" justifyContent="center" gap={3}>
					<Box>
						<Box as="button" p={1} cursor="pointer" onClick={() => setPage(1)}>
							<MdKeyboardDoubleArrowLeft size={20} />
						</Box>

						<Box
							as="button"
							p={1}
							cursor="pointer"
							onClick={() => setPage(Math.max(page - 1, 1))}
						>
							<MdKeyboardArrowLeft size={20} />
						</Box>
					</Box>

					<Box display="flex" gap={2} fontSize="sm">
						{visiblePages.map((p) => (
							<Box
								as="button"
								key={p}
								py={1.5}
								fontSize="sm"
								cursor="pointer"
								width={6}
								borderRadius={6}
								bg={
									p === page
										? bg
										: {
											base: 'white',
											_dark: 'gray.100',
										}
								}
								color={p === page ? color : '{colors.input.text}'}
								onClick={() => setPage(p)}
							>
								{p}
							</Box>
						))}
					</Box>

					<Box>
						<Box
							as="button"
							p={1}
							cursor="pointer"
							onClick={() => setPage(Math.min(page + 1, totalPages))}
						>
							<MdKeyboardArrowRight size={20} />
						</Box>

						<Box
							as="button"
							p={1}
							cursor="pointer"
							onClick={() => setPage(totalPages)}
						>
							<MdKeyboardDoubleArrowRight size={20} />
						</Box>
					</Box>
				</Box>
			</GridItem>

			<GridItem colSpan={{ base: 1, md: 2 }}>
				<Box
					display="flex"
					alignItems="center"
					justifyContent={{ base: 'center', md: 'flex-end' }}
					gap={2}
				>
					<FaceliftText fontSize="sm">Per Page</FaceliftText>
					<FaceliftSelect
						rounded={true}
						borderRadius={6}
						value={pageSize}
						onChange={onSelectValueChange}
						isSearchable={false}
						items={pageSizes}
					/>
				</Box>
			</GridItem>
		</Grid>
	);
};

export default DataTablePaginations;
